import {
  Configuration,
  EventType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

/**
 * @description Creates a new instance of MSAL PublicClientApplication. This function will initialize the instance and set the active account if there is one. This function has to be called before rendering the app. You should render the app only after the promise is resolved. Example:
```tsx
 import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { AuthMethodProvider, createMsalInstance } from '@cx/login';

createMsalInstance(
  {
    clientId: process.env.CLIENT_ID,
    authority: process.env.AUTHORITY_URL,
    knownAuthorities: process.env.KNOWN_AUTHORITIES.split(','), // this environment variable KNOWN_AUTHORITIES is a comma-separated list of strings. the split method will return an array of strings. NOTE: your variable could be different this is just due to the way the environment variables are set in this project where you can't have an array of strings in the .env file.
    redirectUri: window.location.origin,
  },
  true
).then((msalInstance) => {
  const container = document.getElementById('my-login');
  const root = createRoot(container);
  root.render(
    <AuthMethodProvider
      scopes={process.env.SCOPES.split(',')}
      apiUrl={process.env.API_URL}
      msalInstance={msalInstance}
    >
      <App />
    </AuthMethodProvider>
  );
});
```
 * @param configuration
 * @param enableLogger
 * @returns
 */

export const createMsalInstance = async (
  {
    auth,
    scopes,
  }: {
    auth: Configuration['auth'];
    scopes: Array<string>;
  },
  enableLogger = false
): Promise<PublicClientApplication> => {
  const msalInstance = new PublicClientApplication({
    auth,
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
    system: enableLogger
      ? {
          allowNativeBroker: false, // Disables WAM Broker
          loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
              if (containsPii) {
                return;
              }
              switch (level) {
                case LogLevel.Error:
                  console.error(message);
                  return;
                case LogLevel.Info:
                  console.info(message);
                  return;
                case LogLevel.Verbose:
                  console.debug(message);
                  return;
                case LogLevel.Warning:
                  console.warn(message);
                  break;

                default:
                  break;
              }
            },
          },
        }
      : {},
  });

  return msalInstance.initialize().then(() => {
    window.msalInstance = { current: msalInstance };
    window.scopesRef = { current: scopes };
    window.authenticationRequest = { current: null };
    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      // Account selection logic is app dependent. Adjust as needed for different use cases.
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    // Optional - This will update account state if a user signs in from another tab or window
    msalInstance.enableAccountStorageEvents();

    msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        (event.payload as any).account
      ) {
        const { account } = event.payload as any;
        msalInstance.setActiveAccount(account);
      }
    });
    return msalInstance;
  });
};
